<template>
  <div :class="type" class="product">
    <div class="yellow-line-1"></div>
    <div class="yellow-line-2"></div>
    <div class="yellow-line-3"></div>
    <div class="product-content">
      <div class="product-title">
        {{ $t('payment.product.title') }}
      </div>
      <div class="product-list">
        <div
          class="product-item" 
          :class="{'active': modelValue === item.productCode}" 
          v-for="item in state.products" 
          :key="item.productCode" 
          @click="selectProduct(item)"
        >
          <!-- 常规标签 -->
          <div class="product-item-tag" v-if="item.showTag && !item.isValidActivity">
            <div class="tag-text">
              +{{ (item.giftCoin / item.coin * 100).toFixed(0) }}%
            </div>
          </div>
          <!-- 活动标签 -->
          <div class="product-item-act-tag" :class="locale" v-if="item.showTag && item.isValidActivity">
            <div class="tag-text">
              Event
              +{{ (item.giftCoin / item.coin * 100).toFixed(0) }}%
              <img class="gift-icon" src="@/assets/imgs/gift.png" alt="gift">
            </div>
          </div>
          <div class="prop-wrap" :class="{'no-gift': !item.giftCoin}">
            <div class="prop">
              <div class="img-wrap">
                <img src="@/assets/imgs/icon-coin.png" alt="img">
              </div>
              <div class="num">{{ localeFormatNumber(item.coin) }}</div>
            </div>
            <div class="prop" v-if="item.giftCoin">
              <div class="gift-img-wrap">
                <img src="@/assets/imgs/icon-gift-coin.png" alt="gift-coin">
              </div>
              <div class="num gift-num">{{ localeFormatNumber(item.giftCoin) }}</div>
            </div>
            <div class="gift-text">
              {{ $t('payment.product.gift-single') }}
            </div>
          </div>
          <div class="price-wrap">
            <span class="currency">{{ item.currency }}</span>
            <span class="price">{{ calcProductPrice(item.price) }}</span>
          </div>
          <div class="limit-wrap" v-if="item.isLimit">
            {{ $t('payment.product.limit') }}: {{ item.limitCount }}
          </div>
        </div>
      </div>
      <div class="summary-wrap">
        <div class="title">
          {{ $t('payment.product.summary') }}
        </div>
        <div class="product-info">
          {{ $t('payment.product.selected') }}: {{ localeFormatNumber(state.selectedProduct?.coin) }} {{ $t('payment.product.diamond') }}<span class="gift-coin" v-if="state.selectedProduct?.giftCoin">,{{ $t('payment.product.gift') }} {{ localeFormatNumber(state.selectedProduct?.giftCoin) }} {{ $t('payment.product.diamond') }}</span>
        </div>
        <div class="price-wrap">
          <span class="total">{{ $t('payment.product.total') }}: </span>
          <span class="currency">{{ state.selectedProduct?.currency }}</span>
          <span class="price">{{ calcProductPrice(state.selectedProduct?.price) }}</span>
        </div>
        <div class="pay-btn btn" @click="$emit('confirm')">
          {{ $t('payment.product.confirm') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { isMobile } from '@/utils'
import { reactive, computed, defineProps, defineEmits } from 'vue'
import { formatNumber } from '@/utils'
import { useStore } from 'vuex'
const store = useStore()
const locale = computed(() => store.state.locale)
const type = isMobile ? 'mobile' : 'pc'

const localeFormatNumber = (number) => {
  return formatNumber(number, locale.value)
}

const props = defineProps({
  products: {
    type: Array,
    default: () => []
  },
  modelValue: {
    type: String,
    default: ''
  }
})

const state = reactive({
  products: computed(() => {
    return props.products.map(item => {
      const isValidActivity = checkIsValidActivity(item)
      const showTag = getShowTag(item, isValidActivity)
      const coin = item.coin
      const giftCoin = getGiftCoin(item, isValidActivity)
      const {limitSwitch, limitCount} = getLimit(item, isValidActivity)
      return {
        ...item,
        isValidActivity,
        showTag,
        coin,
        giftCoin,
        isLimit: limitSwitch === 2 && limitCount > 0,
        limitSwitch,
        limitCount
      }
    })
  }),
  selectedProduct: computed(() => {
    return state.products.find(item => item.productCode === props.modelValue)
  }),
})

const checkIsValidActivity = (item) => {
  const activity = item.activity
  if (!activity) {
    return false
  }
  if (activity.activityType !== 1) { // 枪战只支持1首充加赠
    return false
  }
  if (activity.activityDurationType === 1) { // 1限时 2常驻
    const now = Math.floor(Date.now() / 1000)
    if (now >= activity.activityEndTime || now < activity.activityStartTime) { // 不在活动时间内
      return false
    }
  }
  if (activity.limitSwitch === 2) { // 1不限购 2限购
    if (activity.limitCount <= 0) { // 限购次数
      return false
    }
  }
  return true
}

const getShowTag = (item, isValidActivity) => {
  if (!isValidActivity) {
    return item.strategyType === 1 && item.giftTagShowSwitch === 2 && item.giftCoin > 0
  }
  const activity = item.activity
  if (activity.tagShowSwitch === 2) { // 1不显示 2显示
    return true
  }
  return false
}

const getGiftCoin = (item, isValidActivity) => {
  if (!isValidActivity) {
    return item.giftCoin
  }
  const activity = item.activity
  const propsGift = JSON.parse(activity.propsGift)
  let giftCoin = 0
  propsGift.forEach(item => {
    if (item.type === 1 && (item.id === '1' || item.id === '70301030')) {
      giftCoin += item.count
    }
  })
  if (item.giftCoin) {
    giftCoin += item.giftCoin
  }
  return giftCoin
}

const getLimit = (item, isValidActivity) => {
  if (!isValidActivity) {
    return { limitSwitch: item.limitSwitch, limitCount: item.limitCount }
  }
  const activity = item.activity
  return { limitSwitch: activity.limitSwitch, limitCount: activity.limitCount }
}

const emit = defineEmits(['update:modelValue', 'confirm'])

const calcProductPrice = (price) => {
  return localeFormatNumber(price / 100)
}

// const selectedProduct = computed(() => {
//   return props.products.find(item => item.productCode === props.modelValue);
// })

const selectProduct = (product) => {
  if (props.modelValue === product.productCode) {
    return
  }
  emit('update:modelValue', product.productCode)
}
</script>

<style src="./pc_style.less" lang="less" scoped></style>
<style src="./mobile_style.less" lang="less" scoped></style>
